import React from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import DataModule from "@/components/DataModule";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Truck, Jeep, Wrench } from "@phosphor-icons/react";
import { money } from "@/components/common";

export default function Fleet() {
  const { user } = useAuth();
  const canWrite = ["admin", "dispatcher"].includes(user.role);

  return (
    <div>
      <PageHeader title="Fleet" subtitle="Trucks, trailers and maintenance records" />
      <Tabs defaultValue="trucks">
        <TabsList className="bg-white border border-slate-200 mb-4">
          <TabsTrigger value="trucks" data-testid="tab-trucks"><Truck size={16} className="mr-1.5" />Trucks</TabsTrigger>
          <TabsTrigger value="trailers" data-testid="tab-trailers"><Jeep size={16} className="mr-1.5" />Trailers</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench size={16} className="mr-1.5" />Maintenance</TabsTrigger>
        </TabsList>

        <TabsContent value="trucks">
          <DataModule apiPath="trucks" queryKey="trucks" canWrite={canWrite} icon={Truck} testid="truck"
            addLabel="Add Truck" emptyTitle="No trucks yet" emptyHint="Add your power units to track the fleet."
            columns={[
              { key: "unit_number", label: "Unit #", mono: true },
              { key: "make", label: "Make", hideSm: true },
              { key: "model", label: "Model", hideSm: true },
              { key: "year", label: "Year", hideSm: true },
              { key: "plate", label: "Plate", mono: true, hideSm: true },
              { key: "registration_expiry", label: "Reg. Expires", mono: true, hideSm: true },
              { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "unit_number", label: "Unit Number", required: true },
              { key: "status", label: "Status", type: "select", options: ["active", "maintenance", "inactive"], default: "active" },
              { key: "make", label: "Make" }, { key: "model", label: "Model" },
              { key: "year", label: "Year", type: "number" }, { key: "vin", label: "VIN" },
              { key: "plate", label: "License Plate" }, { key: "mileage", label: "Mileage", type: "number" },
              { key: "registration_expiry", label: "Registration Expiry", type: "date" },
              { key: "inspection_expiry", label: "Annual Inspection Expiry", type: "date" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>

        <TabsContent value="trailers">
          <DataModule apiPath="trailers" queryKey="trailers" canWrite={canWrite} icon={Jeep} testid="trailer"
            addLabel="Add Trailer" emptyTitle="No trailers yet" emptyHint="Track your trailers and their types."
            columns={[
              { key: "unit_number", label: "Unit #", mono: true },
              { key: "type", label: "Type", hideSm: true },
              { key: "length", label: "Length", hideSm: true },
              { key: "plate", label: "Plate", mono: true, hideSm: true },
              { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "unit_number", label: "Unit Number", required: true },
              { key: "type", label: "Type", type: "select", options: ["dry_van", "reefer", "flatbed", "step_deck", "car_hauler", "tanker"], default: "dry_van" },
              { key: "status", label: "Status", type: "select", options: ["available", "in_use", "maintenance"], default: "available" },
              { key: "length", label: "Length (ft)" }, { key: "plate", label: "Plate" },
              { key: "registration_expiry", label: "Registration Expiry", type: "date" },
              { key: "inspection_expiry", label: "Annual Inspection Expiry", type: "date" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>

        <TabsContent value="maintenance">
          <DataModule apiPath="maintenance" queryKey="maintenance" canWrite={canWrite} icon={Wrench} testid="maintenance"
            addLabel="Log Maintenance" emptyTitle="No maintenance records" emptyHint="Log repairs and services to track fleet health."
            columns={[
              { key: "unit", label: "Unit", mono: true },
              { key: "service", label: "Service" },
              { key: "date", label: "Date", mono: true, hideSm: true },
              { key: "cost", label: "Cost", render: (r) => <span className="font-mono text-sm text-slate-700">{money(r.cost)}</span> },
              { key: "status", label: "Status" },
            ]}
            fields={[
              { key: "unit", label: "Unit (truck/trailer #)", required: true },
              { key: "service", label: "Service", required: true },
              { key: "date", label: "Date", type: "date" },
              { key: "cost", label: "Cost ($)", type: "number" },
              { key: "vendor", label: "Vendor" },
              { key: "status", label: "Status", type: "select", options: ["scheduled", "in_progress", "completed"], default: "completed" },
              { key: "notes", label: "Notes", type: "textarea", full: true },
            ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
